import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import { omit } from 'lodash';
import { generalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDeleteOrganizationLogoMutation,
  useEditOrganizationMutation,
  useOrganizationLogoQuery,
  useUploadOrganizationLogoMutation,
} from '@common/cache';
import { paths } from '@common/types';
import { useContext, useEffect, useState } from 'react';
import { dataURItoBlob } from '@util/image-utils';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

interface IGeneralFormShape {
  name: string;
  domains: string[];
  domainGenerator?: string;
  google_places_id?: string | null;
  street: string;
  suite?: string | null;
  city: string;
  state: string;
  zip_code: string;
  county?: string | null;
  tin?: string | null;
  guide_participant_id?: string | null;
}

const initialValues: IGeneralFormShape = {
  name: '',
  domains: [],
  domainGenerator: '',
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  zip_code: '',
  county: '',
  tin: '',
  guide_participant_id: '',
};

export const useGeneral = (
  organization: paths[`/organization/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const formMethods = useForm<IGeneralFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IGeneralFormShape, any, any>(generalSchema),
    defaultValues: {
      ...initialValues,
      ...organization,
      ...organization.address,
    },
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { isSubmitting, isDirty, errors, isValid },
  } = formMethods;

  const { mutateAsync, isPending } = useEditOrganizationMutation(
    organization.id,
  );

  const handleAddDomain = (newDomain?: string) => {
    const domains = getValues('domains');
    if (newDomain) setValue('domains', [...domains, newDomain]);
    trigger('domainGenerator');
  };

  const handleRemoveDomain = (index: number) => {
    const domains = getValues('domains');
    domains.splice(index, 1);
    setValue('domains', domains);
    trigger('domainGenerator');
  };

  //Image section
  //The cropped image in base64 format
  const fetchedLogo = useOrganizationLogoQuery({
    organization_id: organization.id,
  });
  const [logo, setLogo] = useState<string>(
    fetchedLogo.data ? URL.createObjectURL(fetchedLogo?.data) : '',
  );

  useEffect(() => {
    reset({
      ...organization,
      ...organization.address,
    });
  }, [organization]);

  useEffect(() => {
    if (fetchedLogo.data) {
      setLogo(URL.createObjectURL(fetchedLogo.data as Blob));
    }
  }, [fetchedLogo.data]);

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  const uploadLogoMutation = useUploadOrganizationLogoMutation(organization.id);

  const handleUploadLogo = async (newLogo: string) => {
    setLogo(newLogo);
    let blob = dataURItoBlob(newLogo);
    let fd = new FormData();
    fd.append('file', blob);

    try {
      toast.info('Logo uploading...');

      await uploadLogoMutation.mutateAsync({
        id: organization.id,
        file: fd,
      });
      toast.success('Logo updated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const deleteLogoMutation = useDeleteOrganizationLogoMutation(organization.id);

  const handleDeleteLogo = async () => {
    await deleteLogoMutation.mutateAsync({ id: organization.id });
    toast.success('Logo deleted');
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IGeneralFormShape) => {
    try {
      await mutateAsync({
        ...omit(values, ['domainGenerator']),
        country: 'US',
        id: organization.id,
      });

      toast.success('Changes saved');
      reset(values);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    await onSubmit();
    setIsFormClean(true);
  };

  return {
    formMethods,
    onSubmit,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    handleAddDomain,
    handleRemoveDomain,
    logo,
    handleUploadLogo,
    handleDeleteLogo,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
